import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { useShallow } from 'zustand/react/shallow';
import {
  getTemporalScalingOverrides,
  updateTemporalScalingOverride,
} from '../../services/api';
import type { TemporalScalingOverrides } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

type Bound = 'min' | 'max';

const BOUNDS: readonly Bound[] = ['min', 'max'];
const INVALID_MESSAGE =
  'Enter a whole number of 0 or more, or leave it empty for Auto.';

/**
 * The value to send for an input: a whole number of 0 or more, `null` for an
 * empty input (clear), or `undefined` for anything else. A number input
 * reports text such as `-` or `1e` as an empty value with `validity.badInput`
 * true, so an empty value alone does not mean "clear".
 */
function readBoundValue(field: HTMLInputElement): number | null | undefined {
  const text = field.value.trim();
  if (text === '') return field.validity.badInput ? undefined : null;
  const value = Number(text);
  return Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}

/**
 * Sets the min and max override of each Temporal worker service. The scaler
 * reads them on every tick; they change production capacity, so the page
 * states the consequences before the table.
 */
const TemporalScalingOverridesPanel: React.FC = () => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );
  const [overrides, setOverrides] = useState<TemporalScalingOverrides | null>(
    null
  );
  // "<service> <bound>" of the row being saved, or the one with a bad input.
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [invalidKey, setInvalidKey] = useState<string | null>(null);
  const fields = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await getTemporalScalingOverrides({
          throwOnError: true,
        });
        if (active) setOverrides(response.data);
      } catch (error) {
        console.error('Failed to load Temporal scaling overrides', error);
        if (active) {
          showError(
            'Overrides not loaded',
            getBusinessErrorMessage(error) ??
              'Unable to load scaling overrides. Please try again.'
          );
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [showError]);

  const save = async (service: string, bound: Bound) => {
    const key = `${service} ${bound}`;
    const field = fields.current[key];
    if (!field) return;
    const value = readBoundValue(field);
    if (value === undefined) {
      setInvalidKey(key);
      return;
    }
    setInvalidKey(null);
    setSavingKey(key);
    try {
      // throwOnError so a refused write reaches the catch; the client's
      // default resolves with an error field and would report success.
      const response = await updateTemporalScalingOverride({
        body: { service, bound, value },
        throwOnError: true,
      });
      setOverrides(response.data);
      showSuccess(
        'Override saved',
        value === null ? `${key} is back to Auto.` : `${key} is now ${value}.`
      );
    } catch (error) {
      console.error('Failed to save Temporal scaling override', error);
      showError(
        'Override not saved',
        getBusinessErrorMessage(error) ??
          'Unable to save the override. Please try again.'
      );
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Temporal Scaling</CardTitle>
        <CardDescription>
          Hold a Temporal worker service above or below what the scaler picks.
          Leave a bound empty for Auto.
        </CardDescription>
      </CardHeader>
      <CardContent className='grid gap-4'>
        <Alert>
          <AlertDescription>
            <ul className='list-disc space-y-1 pl-4'>
              <li>
                Overrides apply only on prod, because only prod runs the scaler.
                On other stages, saving an override has no effect.
              </li>
              <li>
                A min override replaces the backlog floor. While it is set,
                backlog cannot raise the floor above it. Clear it when the
                incident ends.
              </li>
              <li>
                The scaler clamps each override to the prod SST min and max of
                the service.
              </li>
              <li>
                A lower floor lets ECS scale in. Scale-in stops tasks, and the
                activities that run on those tasks stop.
              </li>
              <li>
                A change takes effect at the next scaler tick, within about one
                minute.
              </li>
            </ul>
          </AlertDescription>
        </Alert>
        {overrides && (
          <Table className='min-w-[520px]'>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>Min</TableHead>
                <TableHead>Max</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.keys(overrides.min).map(service => (
                <TableRow key={service}>
                  <TableCell className='font-medium'>{service}</TableCell>
                  {BOUNDS.map(bound => {
                    const key = `${service} ${bound}`;
                    const current = overrides[bound][service] ?? null;
                    return (
                      <TableCell key={bound}>
                        <div className='flex items-center gap-2'>
                          <Input
                            // A new key after a save shows the saved value.
                            key={`${key}-${current ?? 'auto'}`}
                            ref={element => {
                              fields.current[key] = element;
                            }}
                            inputSize='sm'
                            type='number'
                            min={0}
                            step={1}
                            placeholder='Auto'
                            aria-label={key}
                            defaultValue={current ?? ''}
                            disabled={savingKey !== null}
                          />
                          <Button
                            size='sm'
                            aria-label={`Save ${key}`}
                            disabled={savingKey !== null}
                            onClick={() => void save(service, bound)}
                          >
                            Save
                          </Button>
                        </div>
                        {invalidKey === key && (
                          <p className='mt-1 text-xs text-destructive'>
                            {INVALID_MESSAGE}
                          </p>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default TemporalScalingOverridesPanel;
